import React, { useEffect, useRef, useState } from "react";
import axios from "axios";
import { useTranslation } from "react-i18next";
import { useLocation } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { Send, X, ThumbsUp, ThumbsDown } from "lucide-react";
import { Link } from "react-router-dom";
import { AlexAvatar } from "@/components/AlexAvatar";

const API = `${process.env.REACT_APP_BACKEND_URL || ""}/api`;
const STORAGE_KEY = "bionet_chat_session";

const fmtTime = (ts) =>
  new Date(ts || Date.now()).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" });

const INSTANT_RESPONSES = {
  fr: [
    { patterns: ["bonjour", "salut", "bonsoir", "coucou", "hello"], reply: "Bonjour ! Comment puis-je vous aider aujourd'hui ? 😊" },
    { patterns: ["merci", "super merci", "c'est parfait"], reply: "Avec plaisir ! N'hésitez pas si vous avez d'autres questions." },
    { patterns: ["au revoir", "bonne journée", "à bientôt", "bye"], reply: "Au revoir ! Bonne journée à vous. N'hésitez pas à revenir si besoin." },
  ],
  en: [
    { patterns: ["hello", "hi", "good morning", "good evening", "hey"], reply: "Hello! How can I help you today? 😊" },
    { patterns: ["thank you", "thanks", "great thanks"], reply: "You're welcome! Feel free to ask if you have other questions." },
    { patterns: ["goodbye", "bye", "see you", "have a good day"], reply: "Goodbye! Have a great day. Feel free to come back if needed." },
  ],
};

const FloatingChatbot = () => {
  const { t, i18n } = useTranslation();
  const lang = i18n.language?.startsWith("en") ? "en" : "fr";
  const location = useLocation();

  const [open, setOpen] = useState(false);
  const [sessionId, setSessionId] = useState(null);
  const [messages, setMessages] = useState([]);
  const [suggestionsVisible, setSuggestionsVisible] = useState(true);
  const [input, setInput] = useState("");
  const [loading, setLoading] = useState(false);
  const [unreadCount, setUnreadCount] = useState(0);
  const [bounced, setBounced] = useState(false);
  const [pulseActive, setPulseActive] = useState(true);
  const [ratings, setRatings] = useState({});

  const scrollRef = useRef(null);
  const conversationStarted = useRef(false);
  const nudgeSent = useRef(false);
  const lastActivityRef = useRef(null);

  // One-time bounce after 3s
  useEffect(() => {
    const t1 = setTimeout(() => setBounced(true), 3000);
    const t2 = setTimeout(() => setBounced(false), 4000);
    return () => { clearTimeout(t1); clearTimeout(t2); };
  }, []);

  // Seed activity timestamp on mount
  useEffect(() => {
    lastActivityRef.current = Date.now();
  }, []);

  // On mount: restore from sessionStorage or show welcome
  useEffect(() => {
    const saved = sessionStorage.getItem(STORAGE_KEY);
    if (saved) {
      try {
        const { messages: sm, sessionId: si } = JSON.parse(saved);
        if (sm?.length > 1) {
          setMessages(sm);
          setSessionId(si || null);
          setSuggestionsVisible(false);
          conversationStarted.current = true;
          return;
        }
      } catch {}
    }
    setMessages([{ role: "assistant", text: t("chatbot.welcome_message"), ts: Date.now() }]);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Reset welcome on lang change (only if no real conversation)
  useEffect(() => {
    if (conversationStarted.current) return;
    setMessages([{ role: "assistant", text: t("chatbot.welcome_message"), ts: Date.now() }]);
    setSuggestionsVisible(true);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [lang]);

  // Persist session
  useEffect(() => {
    if (messages.length > 0) {
      sessionStorage.setItem(STORAGE_KEY, JSON.stringify({ messages, sessionId }));
    }
  }, [messages, sessionId]);

  // Scroll to bottom
  useEffect(() => {
    if (scrollRef.current) {
      scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
    }
  }, [messages, open, loading]);

  // Clear unread on open
  useEffect(() => {
    if (open) setUnreadCount(0);
  }, [open]);

  // Nudge after 2 minutes of inactivity while closed and no conversation started
  useEffect(() => {
    if (open) return;
    const interval = setInterval(() => {
      if (nudgeSent.current || conversationStarted.current) return;
      if (lastActivityRef.current && Date.now() - lastActivityRef.current > 120000) {
        nudgeSent.current = true;
        setUnreadCount((c) => c + 1);
        setMessages((m) => [
          ...m,
          { role: "assistant", text: t("chatbot.nudge_message"), ts: Date.now(), isNudge: true },
        ]);
      }
    }, 30000);
    return () => clearInterval(interval);
  }, [open, t]);

  const getInstantResponse = (text) => {
    const lower = text.toLowerCase().trim();
    const responses = INSTANT_RESPONSES[lang] || INSTANT_RESPONSES.fr;
    for (const { patterns, reply } of responses) {
      if (patterns.some((p) => lower === p || lower.startsWith(p + " ") || lower.endsWith(" " + p))) {
        return reply;
      }
    }
    return null;
  };

  const parseText = (text) => {
    if (!text) return text;
    const parts = text.split(/(06 49 50 28 01|\+33 6 49 50 28 01|\/devis|\/contact)/g);
    return parts.map((part, i) => {
      if (part === "06 49 50 28 01" || part === "+33 6 49 50 28 01") {
        return <a key={i} href="tel:+33649502801" className="underline opacity-90">{part}</a>;
      }
      if (part === "/devis") {
        return <Link key={i} to="/devis" onClick={() => setOpen(false)} className="underline opacity-90">devis en ligne</Link>;
      }
      if (part === "/contact") {
        return <Link key={i} to="/contact" onClick={() => setOpen(false)} className="underline opacity-90">nous contacter</Link>;
      }
      return part;
    });
  };

  const sendText = async (text) => {
    if (!text || loading) return;
    conversationStarted.current = true;
    lastActivityRef.current = Date.now();
    setSuggestionsVisible(false);
    setMessages((m) => [...m, { role: "user", text, ts: Date.now() }]);
    setInput("");

    const instant = getInstantResponse(text);
    if (instant) {
      setMessages((m) => [...m, { role: "assistant", text: instant, ts: Date.now() }]);
      if (!open) setUnreadCount((c) => c + 1);
      return;
    }

    setLoading(true);
    try {
      const res = await axios.post(`${API}/chat`, {
        session_id: sessionId,
        message: text,
        language: lang,
        page_context: location.pathname,
      });
      setSessionId(res.data.session_id);
      const reply = { role: "assistant", text: res.data.response, ts: Date.now() };
      setMessages((m) => [...m, reply]);
      if (!open) setUnreadCount((c) => c + 1);
    } catch {
      setMessages((m) => [
        ...m,
        { role: "assistant", text: t("chatbot.error_message"), ts: Date.now(), isError: true },
      ]);
    } finally {
      setLoading(false);
    }
  };

  const send = () => sendText(input.trim());
  const onKey = (e) => {
    if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); send(); }
  };

  const rate = (idx, value) => setRatings((r) => ({ ...r, [idx]: value }));

  const suggestions = t("chatbot.suggestions", { returnObjects: true }) || [];
  const hasDevisLink = (text) => text?.includes("/devis");
  const hasPhone = (text) =>
    text?.includes("06 49 50 28 01") || text?.includes("+33 6 49 50 28 01");
  const lastAssistantIdx = messages.reduce((acc, m, i) => m.role === "assistant" ? i : acc, -1);

  return (
    <>
      {/* Floating toggle button */}
      <div className="fixed bottom-6 right-5 z-50 flex flex-col items-end gap-1">
        <motion.button
          animate={bounced ? { y: [0, -10, 0, -5, 0] } : {}}
          transition={{ duration: 0.8, ease: "easeInOut" }}
          onClick={() => setOpen((v) => !v)}
          title={t("chatbot.tooltip")}
          className="relative inline-flex items-center gap-2.5 rounded-full bg-gradient-to-r from-brand-green to-brand-blue text-white pl-2.5 pr-5 py-2.5 shadow-xl shadow-brand-green/30 hover:shadow-2xl hover:scale-[1.03] transition"
          aria-label={open ? t("chatbot.close") : t("chatbot.open")}
          data-testid="chatbot-toggle"
        >
          {!open && pulseActive && (
            <motion.span
              className="absolute inset-0 rounded-full bg-brand-green/40 pointer-events-none"
              initial={{ scale: 1, opacity: 0.5 }}
              animate={{ scale: [1, 1.5, 1], opacity: [0.5, 0, 0.5] }}
              transition={{ duration: 2, repeat: 2, ease: "easeInOut" }}
              onAnimationComplete={() => setPulseActive(false)}
            />
          )}
          <span className="flex h-9 w-9 items-center justify-center rounded-full bg-white/20 overflow-hidden flex-shrink-0">
            {open ? <X className="h-5 w-5" /> : <AlexAvatar className="h-9 w-9" />}
          </span>
          <span className="text-sm font-semibold whitespace-nowrap">
            {open ? t("chatbot.close") : t("chatbot.cta_label")}
          </span>
          {!open && unreadCount > 0 && (
            <span className="absolute -top-1 -right-1 h-5 w-5 rounded-full bg-red-500 text-white text-[10px] font-bold flex items-center justify-center">
              {unreadCount}
            </span>
          )}
        </motion.button>
        {!open && (
          <div className="flex items-center gap-1.5 mr-1">
            <span className="relative flex h-2 w-2">
              <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-emerald-400 opacity-75" />
              <span className="relative inline-flex rounded-full h-2 w-2 bg-emerald-500" />
            </span>
            <span className="text-[10px] text-slate-500 font-medium">{t("chatbot.online_status")}</span>
          </div>
        )}
      </div>

      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ opacity: 0, y: 20, scale: 0.96 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: 20, scale: 0.96 }}
            transition={{ duration: 0.25 }}
            className="fixed bottom-0 right-0 sm:bottom-24 sm:right-6 z-50 w-full sm:w-[380px] h-[70vh] sm:h-[580px] max-h-[80vh] rounded-t-2xl sm:rounded-2xl bg-white border border-slate-200 shadow-2xl overflow-hidden flex flex-col"
            data-testid="chatbot-panel"
          >
            {/* Header -brand-green */}
            <div className="flex items-center gap-3 px-4 py-3 bg-brand-green flex-shrink-0">
              <div className="relative flex-shrink-0">
                <div className="h-9 w-9 rounded-full bg-white/20 flex items-center justify-center overflow-hidden">
                  <AlexAvatar className="h-9 w-9" />
                </div>
                <span className="absolute bottom-0 right-0 h-2.5 w-2.5 rounded-full bg-emerald-300 ring-2 ring-brand-green" />
              </div>
              <div className="leading-tight flex-1 min-w-0">
                <div className="font-heading font-bold text-sm text-white truncate">
                  {t("chatbot.header_title")}
                </div>
                <div className="text-xs text-emerald-100">{t("chatbot.header_subtitle")}</div>
              </div>
              <button
                onClick={() => setOpen(false)}
                className="p-1.5 rounded-full hover:bg-white/20 transition text-white/80 flex-shrink-0"
                aria-label={t("chatbot.close")}
              >
                <X className="h-4 w-4" />
              </button>
            </div>

            {/* Messages */}
            <div ref={scrollRef} className="flex-1 overflow-y-auto p-4 space-y-4 bg-slate-50">
              {messages.map((m, i) => (
                <motion.div
                  key={i}
                  initial={{ opacity: 0, y: 6 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.2 }}
                  className={`flex gap-2 ${m.role === "user" ? "justify-end" : "justify-start"}`}
                >
                  {m.role === "assistant" && (
                    <div className="h-6 w-6 rounded-full bg-brand-green/10 flex items-center justify-center flex-shrink-0 mt-0.5 overflow-hidden">
                      <AlexAvatar className="h-6 w-6" />
                    </div>
                  )}
                  <div
                    className={`flex flex-col gap-1 max-w-[80%] ${
                      m.role === "user" ? "items-end" : "items-start"
                    }`}
                  >
                    <div
                      style={m.role === "assistant" ? { whiteSpace: "pre-line" } : undefined}
                      className={`rounded-2xl px-3.5 py-2.5 text-sm leading-relaxed ${
                        m.role === "user"
                          ? "bg-brand-green text-white rounded-br-sm"
                          : "bg-white border border-slate-200 shadow-sm text-slate-800 rounded-bl-sm"
                      }`}
                      data-testid={`chat-msg-${m.role}-${i}`}
                    >
                      {m.role === "assistant" ? parseText(m.text) : m.text}
                    </div>

                    {/* Quick action pills */}
                    {m.role === "assistant" && !m.isNudge && (
                      <div className="flex flex-wrap gap-1.5">
                        {!m.isError && hasDevisLink(m.text) && (
                          <Link
                            to="/devis"
                            onClick={() => setOpen(false)}
                            className="text-xs rounded-full border border-brand-green/50 bg-white text-brand-green px-3 py-1 hover:bg-emerald-50 transition font-medium"
                            data-testid="chat-action-devis"
                          >
                            {t("chatbot.action_devis")}
                          </Link>
                        )}
                        {!m.isError && hasPhone(m.text) && (
                          <a
                            href="tel:+33649502801"
                            className="text-xs rounded-full border border-brand-blue/40 bg-white text-brand-blue px-3 py-1 hover:bg-sky-50 transition font-medium"
                            data-testid="chat-action-call"
                          >
                            {t("chatbot.action_call")}
                          </a>
                        )}
                        {m.isError && (
                          <a
                            href="tel:+33649502801"
                            className="text-xs rounded-full border border-brand-blue/40 bg-white text-brand-blue px-3 py-1 hover:bg-sky-50 transition font-medium"
                          >
                            {t("chatbot.error_cta")}
                          </a>
                        )}
                      </div>
                    )}

                    {/* Ratings -last assistant message only, after conversation started */}
                    {m.role === "assistant" && i === lastAssistantIdx && !m.isError && !m.isNudge && conversationStarted.current && (
                      <div className="flex gap-1 px-1">
                        <button
                          onClick={() => rate(i, "up")}
                          className={`p-1 rounded-full transition ${ratings[i] === "up" ? "text-emerald-600" : "text-slate-300 hover:text-slate-500"}`}
                          aria-label="Utile"
                        >
                          <ThumbsUp className="h-3.5 w-3.5" />
                        </button>
                        <button
                          onClick={() => rate(i, "down")}
                          className={`p-1 rounded-full transition ${ratings[i] === "down" ? "text-red-400" : "text-slate-300 hover:text-slate-500"}`}
                          aria-label="Pas utile"
                        >
                          <ThumbsDown className="h-3.5 w-3.5" />
                        </button>
                      </div>
                    )}

                    <span className="text-[10px] text-slate-400 px-1">{fmtTime(m.ts)}</span>
                  </div>
                </motion.div>
              ))}

              {/* Typing indicator */}
              {loading && (
                <motion.div
                  initial={{ opacity: 0, y: 6 }}
                  animate={{ opacity: 1, y: 0 }}
                  className="flex gap-2 justify-start"
                >
                  <div className="h-6 w-6 rounded-full bg-brand-green/10 flex items-center justify-center flex-shrink-0 mt-0.5 overflow-hidden">
                    <AlexAvatar className="h-6 w-6" />
                  </div>
                  <div className="bg-white border border-slate-200 shadow-sm rounded-2xl rounded-bl-sm px-3.5 py-3">
                    <span className="inline-flex gap-1 items-center">
                      <span className="h-1.5 w-1.5 rounded-full bg-slate-400 animate-bounce" />
                      <span className="h-1.5 w-1.5 rounded-full bg-slate-400 animate-bounce [animation-delay:120ms]" />
                      <span className="h-1.5 w-1.5 rounded-full bg-slate-400 animate-bounce [animation-delay:240ms]" />
                    </span>
                  </div>
                </motion.div>
              )}
            </div>

            {/* Suggestions zone -between messages and input */}
            <AnimatePresence>
              {suggestionsVisible && suggestions.length > 0 && (
                <motion.div
                  initial={{ opacity: 0, height: 0 }}
                  animate={{ opacity: 1, height: "auto" }}
                  exit={{ opacity: 0, height: 0 }}
                  transition={{ duration: 0.2 }}
                  className="px-3 pt-2 pb-1.5 bg-slate-50 border-t border-slate-100 flex-shrink-0"
                >
                  <div className="flex flex-wrap gap-1.5">
                    {suggestions.map((s, i) => (
                      <button
                        key={i}
                        onClick={() => sendText(s)}
                        className="text-xs rounded-full border border-brand-green/40 bg-white text-brand-green px-3.5 py-1.5 hover:bg-emerald-50 transition-colors font-medium"
                        data-testid={`chat-suggestion-${i}`}
                      >
                        {s}
                      </button>
                    ))}
                  </div>
                  <Link
                    to="/contact"
                    onClick={() => setOpen(false)}
                    className="mt-1.5 inline-block text-[11px] text-slate-400 hover:text-brand-green transition underline"
                  >
                    {t("chatbot.callback_link")}
                  </Link>
                </motion.div>
              )}
            </AnimatePresence>

            {/* Input area */}
            <div className="border-t border-slate-200 bg-white flex-shrink-0">
              <div className="flex gap-2 px-3 pt-3 pb-2">
                <input
                  type="text"
                  value={input}
                  onChange={(e) => setInput(e.target.value)}
                  onKeyDown={onKey}
                  placeholder={t("chatbot.placeholder")}
                  className="flex-1 rounded-full border border-slate-200 px-4 py-2 text-sm outline-none focus:border-brand-green focus:ring-2 focus:ring-brand-green/30"
                  data-testid="chat-input"
                />
                <button
                  onClick={send}
                  disabled={loading || !input.trim()}
                  className="rounded-full bg-brand-green text-white p-2 hover:bg-brand-green/90 disabled:opacity-50 transition flex-shrink-0"
                  aria-label={t("chatbot.send")}
                  data-testid="chat-send-button"
                >
                  <Send className="h-4 w-4" />
                </button>
              </div>
              <p className="text-center text-[10px] text-slate-400 pb-2.5">
                {t("chatbot.disclaimer")}
              </p>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
};

export default FloatingChatbot;
